import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CollectionShowcase } from "@/components/home/CollectionShowcase";
import { EventBanners } from "@/components/home/EventBanners";
import { HeroShowroom } from "@/components/home/HeroShowroom";
import { RankRail } from "@/components/home/RankRail";
import { SeasonStrip } from "@/components/home/SeasonStrip";
import { TodayStrip } from "@/components/home/TodayStrip";
import { usePrefersReducedMotion } from "@/components/home/usePrefersReducedMotion";
import { seedProfileSummary } from "@/components/layout/useProfile";
import { Reveal } from "@/components/renewal/fx/Reveal";
import { pullTile } from "@/components/renewal/shop/pullTiles";
import { api, type HomeSummary } from "@/lib/api";
import { authClient, signInWithTelegram } from "@/lib/auth";
import { resolveAuthenticated } from "@/lib/devConfig";
import { useLivePulls } from "@/lib/renewal/livePulls";


/**
 * 섹션 아키텍처 v2 (home-ui-spec "섹션 아키텍처 v2" 절) — UX 루프 순서, 섹션당
 * 목적 1개·주 CTA 1개: S1 SHOWROOM(히어로) → S2 TODAY(행동 3셀) → S3 MY SEASON
 * (시즌 스트립) → S4 LIVE PULLS(레일) → S5 MY COLLECTION(풀폭 쇼케이스) →
 * 로드 시퀀스는 §7.1(S1 내부 + S2 페이드),
 * S3 이하는 Reveal 1회성 스크롤 리빌(§7.2, stagger 0/60/120). 상시 루프는
 * S1 CardSwap 1개. 잔액·환산가치 숫자는 첫 뷰포트 금지 유지.
 */
export function HomePage() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const authenticated = resolveAuthenticated(session?.user);
  const reduced = usePrefersReducedMotion();

  const handleSignIn = useCallback(() => {
    void signInWithTelegram("/home").catch((err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Telegram sign-in failed");
    });
  }, []);

  // 시즌 스트립의 총 IP — 여기서 1회 호출하고 top-bar 프로필 캐시에 시드해
  // 같은 RPC의 중복 발사를 막는다.
  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const cacheKey = session?.user?.id ?? "dev-bypass";
  useEffect(() => {
    if (!authenticated) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    api
      .getHomeSummary()
      .then((next) => {
        if (cancelled) return;
        setSummary(next);
        seedProfileSummary(cacheKey, { ip: next.ip, rp: next.rp, isAdmin: next.isAdmin });
      })
      .catch(() => {
        // 실패 시 스트립은 "—" 유지 — top-bar 쪽 재시도 경로가 별도로 존재한다.
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated, cacheKey]);

  /* S4 LIVE PULLS — 시뮬레이션 라이브 피드(livePulls.ts, 팩 상세 Recent pulls와
   * 단일 스트림). 타일 문법 불변: 하단 행 = [출처 팩 아이콘+팩명 · 상대시간]. */
  const pullTiles = useLivePulls().map(pullTile);

  const todayStrip = <TodayStrip onSignIn={handleSignIn} />;

  return (
    <div className="space-y-10 sm:space-y-14">
      {/* S1 SHOWROOM — 시즌 눈썹이 화면 최상단 첫 텍스트(§1) */}
      <HeroShowroom />

      {/* S1.5 EVENT BOARD — 웹게임 이벤트 배너 캐러셀 (Courtyard 홈 문법) */}
      <EventBanners />

      {/* S2 TODAY — 로드 시퀀스 3번 슬롯: 240ms 지연, opacity 500ms(§7.1) */}
      {reduced ? (
        todayStrip
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
        >
          {todayStrip}
        </motion.div>
      )}

      {/* S3 MY SEASON — 넘버링 눈썹(§2 A3) + 시즌 스트립(Next tier in 포함) */}
      <Reveal>
        <section aria-label="My season">
          <p className="ty-eyebrow mb-3">#2 My season</p>
          <SeasonStrip ip={summary?.ip ?? null} />
        </section>
      </Reveal>

      {/* S4 LIVE PULLS — 라이브 도착 애니메이션(RankRail live), Reveal 1회성. 넘버링 눈썹(§2 A3) */}
      <Reveal delay={60}>
        <div>
          <p className="ty-eyebrow mb-3 flex items-center gap-2">
            <span>#3 Live pulls</span>
            {/* 라이브 인디케이터 — 골드 펄스 점(reduced-motion 시 정적 점) */}
            <span aria-hidden className="relative flex size-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex size-1.5 rounded-full bg-accent" />
            </span>
          </p>
          <RankRail ariaLabel="Live pulls" to="/shop" tiles={pullTiles} live />
        </div>
      </Reveal>

      {/* S5 MY COLLECTION — 벤토 해체: 풀폭 단독 섹션(수집률 카운터는 헤더) */}
      <Reveal>
        <CollectionShowcase
          loading={sessionPending}
          authenticated={authenticated}
          onSignIn={handleSignIn}
        />
      </Reveal>

    </div>
  );
}
